import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiCall as apiService, apiClient, projectService, userService, releaseService } from '../../services/api';

// Generic API call thunk
export const apiCall = createAsyncThunk(
  'api/apiCall',
  async ({ endpoint, method = 'GET', data = null }, { rejectWithValue }) => {
    try {
      const response = await apiService(endpoint, method, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'API call failed');
    }
  }
);

// Specific API calls for common operations
export const fetchProjects = createAsyncThunk(
  'api/fetchProjects',
  async (params, { rejectWithValue }) => {
    try {
      const response = await projectService.getAll();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch projects');
    }
  }
);

export const fetchUsers = createAsyncThunk(
  'api/fetchUsers',
  async (params, { rejectWithValue }) => {
    try {
      const response = await userService.getAll();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch users');
    }
  }
);

// Fetch investment details for a user (uses apiClient for auth header + 401 handling)
export const fetchInvestmentDetails = createAsyncThunk(
  'api/fetchInvestmentDetails',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/v1/get-investment-details/${userId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch investment details');
    }
  }
);

// Fetch participants
export const fetchParticipants = createAsyncThunk(
  'api/fetchParticipants',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/v1/get-participant');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch participants');
    }
  }
);

// Fetch LPs from mst_lp_info
export const fetchLPs = createAsyncThunk(
  'api/fetchLPs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/v1/get-lps');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch LPs');
    }
  }
);

// Fetch CoinGecko prices
export const fetchCoinGeckoPrices = createAsyncThunk(
  'api/fetchCoinGeckoPrices',
  async (symbols, { rejectWithValue }) => {
    try {
      if (!symbols || symbols === '') {
        return [];
      }
      const response = await axios.get(
        `https://api.coingecko.com/api/v3/coins/markets?vs_currency=usd&ids=${symbols}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch CoinGecko prices');
    }
  }
);

// Fetch vesting schedule
export const fetchVestingSchedule = createAsyncThunk(
  'api/fetchVestingSchedule',
  async (_, { rejectWithValue }) => {
    try {
      const response = await releaseService.getVestingSchedule();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch vesting schedule');
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  data: null,
  projects: [],
  users: [],
  investmentDetails: [],
  participants: [],
  lps: [],
  coinGeckoData: [],
  vestingSchedule: [],
};

const apiSlice = createSlice({
  name: 'api',
  initialState,
  reducers: {
    clearApiError: (state) => {
      state.error = null;
    },
    clearApiData: (state) => {
      state.data = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Generic API call cases
      .addCase(apiCall.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(apiCall.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
        state.error = null;
      })
      .addCase(apiCall.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Projects cases
      .addCase(fetchProjects.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjects.fulfilled, (state, action) => {
        state.loading = false;
        state.projects = action.payload;
        state.error = null;
      })
      .addCase(fetchProjects.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Users cases
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
        state.error = null;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Investment details cases
      .addCase(fetchInvestmentDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvestmentDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.investmentDetails = action.payload;
        state.error = null;
      })
      .addCase(fetchInvestmentDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Participants cases
      .addCase(fetchParticipants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchParticipants.fulfilled, (state, action) => {
        state.loading = false;
        state.participants = action.payload;
        state.error = null;
      })
      .addCase(fetchParticipants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // LPs cases
      .addCase(fetchLPs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLPs.fulfilled, (state, action) => {
        state.loading = false;
        state.lps = action.payload;
        state.error = null;
      })
      .addCase(fetchLPs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // CoinGecko cases
      .addCase(fetchCoinGeckoPrices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCoinGeckoPrices.fulfilled, (state, action) => {
        state.loading = false;
        state.coinGeckoData = action.payload;
        state.error = null;
      })
      .addCase(fetchCoinGeckoPrices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Vesting schedule cases
      .addCase(fetchVestingSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVestingSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.vestingSchedule = action.payload;
        state.error = null;
      })
      .addCase(fetchVestingSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearApiError, clearApiData } = apiSlice.actions;
export default apiSlice.reducer;
